import { hasReviewedReport, loadReviewedReportHistory, reportDeliveryLabel, recordedReportSentAt, reportDisplayTime } from '../lib/reportHistory'
import ReportDeliveryHistory from './ReportDeliveryHistory'
import PaymentRequestEmail from './PaymentRequestEmail'
import { isOutboundTestMode } from '../lib/stagingEnvironment'
import React, { useEffect, useState } from 'react'
import StatusPill from './StatusPill'
import { supabase } from '../lib/supabase'
import { fmtDate, fmtDuration, generateCustomerReport, isGeneralJob, jobFinancials, jobPaymentActionState, jobPaymentMeta, jobProgressMeta, jobServiceSummary, jobServiceTemplateName, moneyNumber, normaliseJobStatus, normalisePriceAdjustments, priceAdjustmentAmount, rockShoxTrailheadUrl, jobStatusMeta } from '../lib/utils'
import { LOYALTY_WORKSHOP_COLUMNS, jobCanReceiveLoyaltyReward, loyaltyProgressForCustomer, loyaltyRuleSummary } from '../lib/loyalty'
import { useAuth } from '../lib/AuthContext'
import { canRunJobLifecycleAction, lifecycleNoteForAction, runJobLifecycleAction } from '../lib/jobLifecycle'
import { formatPlannedMinutes, plannedMinutesForJob } from '../lib/capacityPlanning'
import { printJobTag } from '../lib/jobTags'
import GeneralJobModal from './GeneralJobModal'
import JobEvidence from './JobEvidence'
import JobTimeline from './JobTimeline'
import AdministrativeClosePanel from './AdministrativeClosePanel'
import { suspensionAdministrativeCloseMissing } from '../lib/administrativeClose'

const STANDARD_CONNECT_MESSAGE = 'Stripe card payments use the workshop connected Stripe Standard account. Bank transfer and manual paid tracking remain available.'

function normaliseTemplateArray(value) {
  if (!value) return []
  if (Array.isArray(value)) return value
  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function normaliseObject(value) {
  if (!value) return {}
  if (typeof value === 'object' && !Array.isArray(value)) return value
  try {
    const parsed = JSON.parse(value)
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
  } catch {
    return {}
  }
}

function normaliseTemplateSnapshot(value) {
  if (!value) return null
  if (typeof value === 'object') return value
  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}

function normaliseMeasurementSections(value) {
  return normaliseTemplateArray(value)
    .filter(section => section && typeof section === 'object')
    .map(section => ({
      key: section.key || section.title || 'measurement_section',
      title: section.title || 'Measurements',
      description: section.description || '',
      category: section.category || 'service_log',
      fields: normaliseTemplateArray(section.fields).filter(field => field && typeof field === 'object'),
    }))
    .filter(section => section.fields.length > 0)
}

function SuspensionViewJobModal({ job: initialJob, customer, component: comp, workshop, services = [], onClose, onEdit, onCreateComponent, onChanged, returnLabel = 'Close' }) {
  const { profile, workshopId, workshop: authWorkshop } = useAuth()
  const outboundTestMode = isOutboundTestMode()
  const effectiveWorkshop = workshop || authWorkshop || {}
  const [j, setJob] = useState(initialJob)
  useEffect(() => { setJob(initialJob) }, [initialJob])

  const financials = jobFinancials(j)
  const unresolvedChargeableStockCount = Number(financials.unresolvedChargeableStockCount || 0)
  const partsUsed = financials.partsUsed || []
  const selectedServices = jobServiceSummary(j)
  const serviceTemplateName = jobServiceTemplateName(j)
  const serviceTemplateSnapshot = normaliseTemplateSnapshot(j.service_template_snapshot)
  const measurementSections = normaliseMeasurementSections(serviceTemplateSnapshot?.measurement_schema)
  const measurementSettingSections = measurementSections.filter(section => section.category === 'settings')
  const measurementServiceLogSections = measurementSections.filter(section => section.category !== 'settings')
  const status = jobStatusMeta(j)
  const payment = jobPaymentMeta({
    ...j,
    payment_link: j.payment_refresh_required ? '' : (j.payment_link || initialJob.payment_link),
  })
  const payableAmountPence = Math.round(Math.max(0, Number(financials.totalCost || 0)) * 100)
  const statusKey = normaliseJobStatus(j.status)
  const isCompleted = statusKey === 'completed'
  const isCancelled = statusKey === 'cancelled'
  const isOwner = profile?.role === 'owner' || profile?.is_superadmin
  const canMarkReady = canRunJobLifecycleAction(profile, 'ready')
  const canMarkPaidManually = canRunJobLifecycleAction(profile, 'manual_paid')
  const canCloseJob = canRunJobLifecycleAction(profile, 'close')
  const canReopenJob = canRunJobLifecycleAction(profile, 'reopen')
  const administrativeCloseMissing = suspensionAdministrativeCloseMissing(j)
  const adjustmentRows = normalisePriceAdjustments(j)
  const adjustmentLocked = payment.key === 'paid'
  const progressItems = jobProgressMeta(j)
  const [evidenceItems, setEvidenceItems] = useState([])
  const [reportVersions, setReportVersions] = useState([])
  const [reportHistoryError, setReportHistoryError] = useState('')
  const [reportBusy, setReportBusy] = useState('')
  const [reportNotice, setReportNotice] = useState('')

  // Email report state
  const [emailMode, setEmailMode] = useState(false)
  const [emailTo, setEmailTo] = useState(customer?.email || '')
  const [sending, setSending] = useState(false)
  const [emailStatus, setEmailStatus] = useState(null)
  const [emailError, setEmailError] = useState('')

  // Payment link state
  const [generatingLink, setGeneratingLink] = useState(false)
  const [paymentLink, setPaymentLink] = useState(j.payment_link || null)
  const [linkError, setLinkError] = useState('')
  const [linkCopied, setLinkCopied] = useState(false)
  const [paymentBusy, setPaymentBusy] = useState('')
  const [paymentNotice, setPaymentNotice] = useState('')
  const [paymentError, setPaymentError] = useState('')
  const [adjustmentBusy, setAdjustmentBusy] = useState('')
  const [adjustmentNotice, setAdjustmentNotice] = useState('')
  const [adjustmentError, setAdjustmentError] = useState('')
  const [loyaltyJobs, setLoyaltyJobs] = useState([])
  const [loyaltyComponents, setLoyaltyComponents] = useState([])
  const [loyaltyRewards, setLoyaltyRewards] = useState([])
  const [loyaltyBusy, setLoyaltyBusy] = useState(false)
  const [loyaltyWorkshop, setLoyaltyWorkshop] = useState(effectiveWorkshop)
  const [tagError, setTagError] = useState('')
  const [tagBusy, setTagBusy] = useState(false)
  const effectiveLoyaltyWorkshop = { ...effectiveWorkshop, ...(loyaltyWorkshop || {}) }
  const activePaymentLink = j.payment_refresh_required ? '' : paymentLink
  const paymentEmailState = jobPaymentActionState({ ...j, payment_link: activePaymentLink }, financials)
  const loyaltyProgress = loyaltyProgressForCustomer({
    workshop: effectiveLoyaltyWorkshop,
    jobs: loyaltyJobs,
    components: loyaltyComponents,
    rewards: loyaltyRewards,
    customerId: customer?.id || j.customer_id,
  })
  const canApplyLoyaltyByRole = isOwner || effectiveLoyaltyWorkshop?.discount_approval_required !== true
  const loyaltyAlreadyAppliedToJob = loyaltyRewards.some(reward => reward.status === 'applied' && reward.applied_job_id === j.id)
  const loyaltyEligibleJob = jobCanReceiveLoyaltyReward(j, comp, effectiveLoyaltyWorkshop)

  function fmtDateTime(value) {
    if (!value) return ''
    return new Date(value).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })
  }

  function fmtMoney(value) {
    return '£' + Number(value || 0).toFixed(2)
  }

  useEffect(() => {
    let cancelled = false

    async function loadReportVersions() {
      if (!j.id) return
      setReportHistoryError('')
      const { data, error } = hasReviewedReport(j) ? await loadReviewedReportHistory(supabase, j) : await supabase
        .from('job_report_versions')
        .select('*')
        .eq('job_id', j.id)
        .order('version_number', { ascending: false })

      if (cancelled) return
      if (error) {
        setReportVersions([])
        setReportHistoryError(error.message)
        return
      }
      setReportVersions(data || [])
    }

    loadReportVersions()
    return () => { cancelled = true }
  }, [j.id])

  useEffect(() => {
    setPaymentLink(j.payment_link || null)
  }, [j.payment_link])

  async function loadLoyalty() {
    const customerId = customer?.id || j.customer_id
    if (!workshopId) return
    const wsRes = await supabase
      .from('workshops')
      .select(LOYALTY_WORKSHOP_COLUMNS)
      .eq('id', workshopId)
      .single()
    setLoyaltyWorkshop({ ...effectiveWorkshop, ...(wsRes?.data || {}) })
    const enabled = wsRes?.data?.loyalty_enabled === true
    if (!enabled || !customerId) {
      setLoyaltyJobs([])
      setLoyaltyComponents([])
      setLoyaltyRewards([])
      return
    }
    const [jobsRes, componentsRes, rewardsRes] = await Promise.all([
      supabase
        .from('jobs')
        .select('*, job_price_adjustments(*)')
        .eq('workshop_id', workshopId)
        .eq('customer_id', customerId),
      supabase
        .from('components')
        .select('id, customer_id, type')
        .eq('workshop_id', workshopId)
        .eq('customer_id', customerId),
      supabase
        .from('loyalty_rewards')
        .select('*')
        .eq('workshop_id', workshopId)
        .eq('customer_id', customerId)
        .order('created_at', { ascending: false }),
    ])
    setLoyaltyJobs(jobsRes.data || [])
    setLoyaltyComponents(componentsRes.data || [])
    setLoyaltyRewards(rewardsRes.data || [])
  }

  useEffect(() => { loadLoyalty() }, [workshopId, customer?.id, j.customer_id])

  function mergeReportVersion(version) {
    if (!version?.id) return
    setReportVersions(versions => [version, ...versions.filter(item => item.id !== version.id)]
      .sort((a, b) => Number(b.version_number || 0) - Number(a.version_number || 0)))
  }

  async function openReportVersion(version) {
    if (!version?.storage_path) return
    setReportBusy(`open-${version.id}`)
    setReportHistoryError('')
    try {
      const { data, error } = await supabase.storage
        .from(version.storage_bucket || 'job-reports')
        .createSignedUrl(version.storage_path, 60 * 10)
      if (error) throw error
      if (data?.signedUrl) window.open(data.signedUrl, '_blank', 'noopener,noreferrer')
    } catch (err) {
      setReportHistoryError(err.message || 'Could not open report PDF.')
    } finally {
      setReportBusy('')
    }
  }

  async function createReportVersion() {
    setReportBusy('create')
    setReportNotice('')
    setReportHistoryError('')
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      if (!token) throw new Error('Please sign in again before saving a report version.')

      const res = await fetch('/.netlify/functions/create-report-version', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ jobId: j.id }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || 'Could not save report version.')

      mergeReportVersion(data.report_version)
      setReportNotice(`Saved report v${data.report_version?.version_number || ''}.`)
      if (data.signed_url) window.open(data.signed_url, '_blank', 'noopener,noreferrer')
    } catch (err) {
      setReportHistoryError(err.message || 'Could not save report version.')
    } finally {
      setReportBusy('')
    }
  }

  async function generatePaymentLink() {
    if (outboundTestMode) {
      setLinkError('Payment actions are disabled in test mode.')
      return
    }
    if (isCancelled) return
    setGeneratingLink(true)
    setLinkError('')
    setPaymentError('')
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      if (!token) throw new Error('Please sign in again before generating a payment link.')

      const res = await fetch('/.netlify/functions/create-payment-link', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ jobId: j.id }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || 'Failed to generate link')

      setPaymentLink(data.url)
      setJob(job => ({
        ...job,
        payment_link: data.url,
        payment_status: data.payment_status || 'requested',
        payment_requested_at: new Date().toISOString(),
        stripe_checkout_session_id: data.checkout_session_id || job.stripe_checkout_session_id || '',
        payment_refresh_required: false,
        payment_refresh_reason: '',
      }))
      setPaymentNotice('Payment link ready — customer not contacted.')
      if (onChanged) onChanged()
    } catch (err) {
      setLinkError(err.message || 'Failed to generate link')
    } finally {
      setGeneratingLink(false)
    }
  }

  async function copyLink() {
    if (!activePaymentLink) return
    try {
      await navigator.clipboard.writeText(activePaymentLink)
      setLinkCopied(true)
      setPaymentNotice('Payment link copied.')
      setTimeout(() => setLinkCopied(false), 2000)
    } catch {
      setPaymentError('Could not copy payment link in this browser.')
    }
  }

  function paymentMethodLabel(value) {
    const labels = {
      stripe: 'Stripe',
      manual: 'Manual',
      cash: 'Cash',
      card: 'Card',
      bank_transfer: 'Bank transfer',
      other: 'Other',
    }
    return labels[value] || value || ''
  }

  async function runLifecycle(action, options, busyKey, notice) {
    setPaymentBusy(busyKey)
    setPaymentNotice('')
    setPaymentError('')
    try {
      const data = await runJobLifecycleAction(supabase, action, j.id, options)
      const nextJob = data?.job || data
      if (nextJob?.id) {
        setJob(nextJob)
        setPaymentLink(nextJob.payment_link || null)
      } else {
        await reloadJob()
      }
      setPaymentNotice(notice)
      if (onChanged) onChanged()
      return nextJob
    } catch (err) {
      setPaymentError(err.message || 'Could not update the job lifecycle.')
      return null
    } finally {
      setPaymentBusy('')
    }
  }

  async function reloadJob() {
    const { data, error } = await supabase
      .from('jobs')
      .select('*, job_price_adjustments(*)')
      .eq('id', j.id)
      .single()
    if (error) throw error
    setJob(data)
    setPaymentLink(data.payment_link || null)
    return data
  }

  async function syncStoredTotal(job) {
    const total = jobFinancials(job).totalCost
    const { data, error } = await supabase
      .from('jobs')
      .update({ total_charged: total })
      .eq('id', job.id)
      .select('*, job_price_adjustments(*)')
      .single()
    if (error) throw error
    setJob(data)
    setPaymentLink(data.payment_link || null)
    return data
  }

  async function decideAdjustment(row, status) {
    if (!isOwner) return
    if (adjustmentLocked) {
      setAdjustmentError('Paid jobs are locked. Discounts and adjustments cannot be changed.')
      return
    }
    setAdjustmentBusy(`${status}-${row.id}`)
    setAdjustmentNotice('')
    setAdjustmentError('')
    try {
      const { error } = await supabase
        .from('job_price_adjustments')
        .update({ status })
        .eq('id', row.id)
        .eq('job_id', j.id)
      if (error) throw error
      const refreshed = await reloadJob()
      const synced = status === 'approved'
        ? await syncStoredTotal({ ...refreshed, job_price_adjustments: normalisePriceAdjustments(refreshed) })
        : refreshed
      const paymentWarning = synced.payment_refresh_required ? ' Existing payment link was cleared; generate a new one before requesting payment.' : ''
      setAdjustmentNotice(status === 'approved' ? `Adjustment approved.${paymentWarning}` : 'Adjustment rejected.')
      if (onChanged) onChanged()
    } catch (err) {
      setAdjustmentError(err.message || 'Could not update discount or adjustment.')
    } finally {
      setAdjustmentBusy('')
    }
  }

  async function applyLoyaltyReward() {
    if (adjustmentLocked) {
      setAdjustmentError('Paid jobs are locked. Loyalty rewards cannot be applied.')
      return
    }
    if (loyaltyAlreadyAppliedToJob) {
      setAdjustmentError('A loyalty reward has already been applied to this job.')
      return
    }
    if (!loyaltyEligibleJob) {
      setAdjustmentError('This job is not eligible for the current loyalty categories.')
      return
    }
    if (loyaltyProgress.availableCount <= 0) {
      setAdjustmentError('This customer has no available loyalty reward yet.')
      return
    }
    if (!canApplyLoyaltyByRole) {
      setAdjustmentError('Owner approval is required to apply loyalty rewards.')
      return
    }

    setLoyaltyBusy(true)
    setAdjustmentNotice('')
    setAdjustmentError('')
    try {
      const { error } = await supabase.rpc('apply_loyalty_reward', { p_job_id: j.id })
      if (error) throw error
      const refreshed = await reloadJob()
      const synced = await syncStoredTotal({ ...refreshed, job_price_adjustments: normalisePriceAdjustments(refreshed) })
      await loadLoyalty()
      const paymentWarning = synced.payment_refresh_required ? ' Existing payment link was cleared; generate a new one before requesting payment.' : ''
      setAdjustmentNotice(`Loyalty reward applied.${paymentWarning}`)
      if (onChanged) onChanged()
    } catch (err) {
      setAdjustmentError(err.message || 'Could not apply loyalty reward.')
    } finally {
      setLoyaltyBusy(false)
    }
  }

  async function markReadyForCollection() {
    await runLifecycle(
      'ready',
      { note: lifecycleNoteForAction('ready', j) },
      'ready',
      'Job marked ready for collection.'
    )
  }

  async function sendPaymentReminder() {
    if (outboundTestMode) {
      setPaymentError('Outbound payment messages are disabled in test mode.')
      return
    }
    if (j.payment_refresh_required) {
      setPaymentError(j.payment_refresh_reason || 'Generate a new payment link before sending a reminder.')
      return
    }
    const to = customer?.email || ''
    if (!to) {
      setPaymentError('Add a customer email before sending a payment reminder.')
      return
    }
    const firstSend = !j.payment_reminder_sent_at

    setPaymentBusy('reminder')
    setPaymentNotice('')
    setPaymentError('')
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      if (!token) throw new Error('Please sign in again before sending a reminder.')

      const res = await fetch('/.netlify/functions/send-payment-reminder', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ to, jobId: j.id }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || 'Payment reminder failed.')

      const paymentSentAt = data.payment_reminder_sent_at || new Date().toISOString()
      const reportSentAt = data.report_sent_at || paymentSentAt
      setPaymentNotice(firstSend ? `Payment request and report emailed to ${to}.` : `Payment reminder and report emailed to ${to}.`)
      setJob(job => ({
        ...job,
        payment_status: 'requested',
        payment_reminder_sent_at: paymentSentAt,
        report_sent_at: reportSentAt,
      }))
      if (data.report_version) mergeReportVersion(data.report_version)
      if (data.warning) setPaymentError(data.warning)
      if (onChanged) onChanged()
    } catch (err) {
      setPaymentError(err.message || 'Payment reminder failed.')
    } finally {
      setPaymentBusy('')
    }
  }

  async function markPaid() {
    const amountPence = Math.round(Math.max(0, Number(financials.totalCost || 0)) * 100)
    await runLifecycle(
      'manual_paid',
      {
        method: 'manual',
        reference: j.payment_reference || 'Marked paid in Rebound',
        amountPence,
        note: 'Payment marked paid manually from the job view.',
      },
      'paid',
      'Payment marked as paid.'
    )
  }

  async function completeJob() {
    const amountPence = Math.round(Math.max(0, Number(financials.totalCost || 0)) * 100)
    if (amountPence > 0 && payment.key !== 'paid') {
      setPaymentError('Mark the payment as paid before closing the job.')
      return
    }
    await runLifecycle(
      'close',
      { note: lifecycleNoteForAction('close', { ...j, total_charged: financials.totalCost }) },
      'complete',
      'Job completed and closed.'
    )
  }

  async function reopenJob() {
    await runLifecycle(
      'reopen',
      { note: lifecycleNoteForAction('reopen', j) },
      'reopen',
      'Job reopened and moved back to ready for collection.'
    )
  }

  async function sendReport() {
    if (outboundTestMode) {
      setEmailStatus('error')
      setEmailError('Outbound report email is disabled in test mode.')
      return
    }
    if (!emailTo) return
    setSending(true)
    setEmailStatus(null)
    setEmailError('')
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      if (!token) throw new Error('Please sign in again before sending a report.')

      const res = await fetch('/.netlify/functions/send-report', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ to: emailTo, jobId: j.id }),
      })
      const data = await res.json()
      if (!res.ok) {
        setEmailStatus('error')
        setEmailError(data.error || 'Send failed')
      } else {
        setEmailStatus('sent')
        setEmailMode(false)
        const sentAt = recordedReportSentAt(data)
        setJob(job => ({ ...job, report_sent_at: sentAt }))
        if (data.report_version) mergeReportVersion(data.report_version)
        if (data.warning) setReportHistoryError(data.warning)
      }
    } catch (err) {
      setEmailStatus('error')
      setEmailError(err.message)
    }
    setSending(false)
  }

  async function handlePrintTag() {
    setTagError('')
    setTagBusy(true)
    try {
      await printJobTag({ job: j, customer, component: comp, statusLabel: status.label })
    } catch (err) {
      setTagError(err.message || 'Could not prepare the print tag. Allow pop-ups and try again.')
    } finally {
      setTagBusy(false)
    }
  }

  const dr = (label, val) => val
    ? <div key={label} className="detail-row"><span className="detail-label">{label}</span><span className="detail-val">{val}</span></div>
    : null

  const chk = (label, val) => (
    <div key={label} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '3px 0' }}>
      <span>{val ? '✅' : '⬜'}</span><span className="detail-val">{label}</span>
    </div>
  )

  const subh = (label, color) => (
    <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color, margin: '10px 0 5px' }}>{label}</div>
  )

  function measurementValue(field, side = null) {
    if (side === 'before' && field.before) return j[field.before] || ''
    if (side === 'after' && field.after) return j[field.after] || ''
    if (side && field.kind === 'before_after') {
      const measurements = normaliseObject(j.structured_measurements)
      return measurements[`${field.key}_${side}`] || ''
    }
    if (field.field) return j[field.field] || ''
    return normaliseObject(j.structured_measurements)[field.key] || ''
  }

  function displayMeasurementValue(field, side = null) {
    const value = measurementValue(field, side)
    if (!value) return ''
    return field.unit ? `${value} ${field.unit}` : value
  }

  function hasMeasurementValue(field) {
    if (field.kind === 'before_after') return !!(measurementValue(field, 'before') || measurementValue(field, 'after'))
    return !!measurementValue(field)
  }

  function renderMeasurementRows(sections, emptyText) {
    const rendered = sections.map(section => {
      const rows = section.fields.filter(hasMeasurementValue)
      if (!rows.length) return null
      return (
        <div key={section.key} style={{ marginBottom: 12 }}>
          {subh(section.title, 'var(--muted)')}
          {rows.map(field => {
            const internal = field.visibility === 'internal' || field.customer_visible === false
            const label = field.unit && field.kind !== 'before_after' ? `${field.label || field.key} (${field.unit})` : (field.label || field.key)
            const value = field.kind === 'before_after'
              ? `${displayMeasurementValue(field, 'before') || '-'} -> ${displayMeasurementValue(field, 'after') || '-'}`
              : displayMeasurementValue(field)
            return (
              <div key={field.key || field.field || field.label} className="detail-row">
                <span className="detail-label">{label}</span>
                <span className="detail-val">
                  {value}
                  {internal && <span className="job-status-chip job-status-muted" style={{ marginLeft: 8 }}>Internal</span>}
                </span>
              </div>
            )
          })}
        </div>
      )
    }).filter(Boolean)

    if (!rendered.length) return <div className="fs12 text-muted">{emptyText}</div>
    return rendered
  }

  const scopeItems = [
    ['Lower leg service', j.sc_lower_leg], ['Air spring service', j.sc_air_spring],
    ['Damper rebuild', j.sc_damper], ['Air can service', j.sc_air_can],
    ['Full shock service', j.sc_full_shock], ['Bushing / hardware replacement', j.sc_bushing],
    ['Travel change', j.sc_travel], ['Seal upgrade', j.sc_seal_upgrade],
    ['Custom tune / revalve', j.sc_retune], ['Setup session only', j.sc_setup],
    ['Seatpost / dropper service', j.sc_dropper_service]
  ].filter(([, v]) => v).map(([l]) => l)
  const paymentLinkSent = Boolean(j.payment_reminder_sent_at)
  const sendPaymentLabel = paymentLinkSent ? 'Send Reminder' : 'Email Payment Request & Report'
  const plannedMinutes = plannedMinutesForJob(j, { services })
  const priceTotal = moneyNumber(financials.totalCost)
  const priceLabel = priceTotal > 0 ? `£${priceTotal.toFixed(2)}` : 'No charge recorded'
  const reportStatus = j.report_sent_at
    ? `Sent ${fmtDateTime(j.report_sent_at)}`
    : reportVersions.length
      ? `${reportVersions.length} saved PDF${reportVersions.length === 1 ? '' : 's'}`
      : 'Not sent'

  return (
    <div className="modal-overlay">
      <div className="modal modal-wide job-workspace-modal suspension-workspace read-only-job-workspace">
        <header className="suspension-workspace-header">
          <div className="suspension-workspace-topline">
            <button type="button" className="suspension-workspace-back" onClick={onClose}>← {returnLabel}</button>
            <div className="suspension-workspace-actions">
              <a className="btn btn-ghost btn-sm" href={`/jobs/${j.id}/time`} target="_blank" rel="noreferrer" aria-label="Actual time, opens in a new tab">Actual time ↗</a>
              <StatusPill tone={status.tone}>{status.label}</StatusPill>
              <StatusPill tone={payment.tone}>Payment: {payment.label}</StatusPill>
              <button className="close-btn" onClick={onClose}>×</button>
            </div>
          </div>
          <div className="suspension-workspace-title-row">
            <div>
              <div className="refresh-kicker">Read-only job · {j.job_number || 'Suspension'}</div>
              <div className="modal-title">{comp ? (comp.year ? comp.year + ' ' : '') + comp.model : 'Booking job'}</div>
              <div className="text-muted fs12">{customer ? `${customer.first_name} ${customer.last_name}` : 'Customer not linked'} · {fmtDate(j.date)}</div>
            </div>
            <div className="read-only-job-price"><span>Recorded total</span><strong>{priceLabel}</strong></div>
          </div>
        </header>

        <div className="sec" style={{ marginTop: 0 }}>Job Details</div>
        {dr('Job Number', j.job_number || 'Suspension job')}
        {dr('Invoice Number', j.invoice_number || 'Not issued')}
        {dr('Customer', customer ? customer.first_name + ' ' + customer.last_name : '—')}
        <div className="detail-row">
          <span className="detail-label">Status</span>
          <span className="detail-val">
            <StatusPill tone={status.tone}>{status.label}</StatusPill>
            <span className="text-muted fs12" style={{ marginLeft: 8 }}>Next: {status.nextAction}</span>
          </span>
        </div>
        {dr('Component', comp ? (comp.year ? comp.year + ' ' : '') + comp.model : '—')}
        {dr('Received Date', j.date ? fmtDate(j.date) : 'Not recorded')}
        {dr('Date Booked In For', j.scheduled_date ? fmtDate(j.scheduled_date) : 'Not scheduled')}
        {dr('Date For Collection / Returned', j.due_date ? fmtDate(j.due_date) : 'Not set')}
        {dr('Bench / Location', j.physical_location || 'Not set')}
        {dr('Selected Services', selectedServices)}
        {dr('Technician', j.technician || 'Unassigned')}
        {dr('Planned Time', plannedMinutes ? formatPlannedMinutes(plannedMinutes) : 'Time required')}
        {dr('Price', priceLabel)}
        {dr('Payment Status', payment.label)}
        {dr('Report Status', reportStatus)}
        <div className="detail-row">
          <span className="detail-label">Progress</span>
          <span className="detail-val">
            <span className="action-row">
              {progressItems.map(item => (
                <StatusPill key={item.key} tone={item.tone}>
                  {item.label}: {item.value}
                </StatusPill>
              ))}
            </span>
          </span>
        </div>
        {onEdit && (
          <div className="action-row" style={{ margin: '8px 0 16px' }}>
            <button className="btn btn-primary btn-sm" onClick={onEdit}>Schedule / Assign</button>
          </div>
        )}

        <div className="sec">Job Timeline</div>
        <JobTimeline
          job={j}
          customer={customer}
          component={comp}
          evidenceItems={evidenceItems}
          reportVersions={reportVersions}
          compact
        />

        <div className="sec">Component Details</div>
        {dr('Serial', comp?.serial)}
        {dr('Service Template', serviceTemplateName)}

        {j.inspection_completed_at && dr('Inspection Completed', fmtDateTime(j.inspection_completed_at))}
        {dr('Inspection Summary', j.inspection_summary)}
        {j.estimate_amount !== null && j.estimate_amount !== undefined && dr('Additional Quote', '£' + Number(j.estimate_amount || 0).toFixed(2))}
        {Number(j.estimate_parts_cost || 0) > 0 && dr('Additional Parts Cost', '£' + Number(j.estimate_parts_cost || 0).toFixed(2))}
        {j.estimate_sent_at && dr('Quote Sent', fmtDateTime(j.estimate_sent_at))}
        {j.customer_decision && dr('Customer Decision', `${j.customer_decision}${j.customer_decision_at ? ' - ' + fmtDateTime(j.customer_decision_at) : ''}`)}
        {dr('Decision Note', j.customer_decision_note)}
        {j.payment_paid_at && dr('Payment Paid', fmtDateTime(j.payment_paid_at))}
        {j.start_time && j.finish_time && dr('Start / Finish', j.start_time + ' → ' + j.finish_time)}
        {dr('Duration', j.duration_mins ? fmtDuration(j.duration_mins) : null)}

        {scopeItems.length > 0 && (
          <>
            <div className="sec">Service Scope</div>
            {scopeItems.map(s => (
              <div key={s} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '3px 0' }}>
                <span>✅</span><span className="detail-val">{s}</span>
              </div>
            ))}
          </>
        )}

        <div className="sec">Settings</div>
        {measurementSettingSections.length ? renderMeasurementRows(measurementSettingSections, 'No settings recorded yet.') : (
          <div className="grid-2">
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>Before</div>
              {subh('Spring', 'var(--muted)')}
              {dr('Air Pressure / Spring', j.b_air)}{dr('Sag %', j.b_sag)}{dr('Coil Preload', j.b_preload)}{dr('Tokens / Spacers', j.b_tokens)}
              {subh('Compression', 'var(--muted)')}
              {dr('LSC', j.b_lsc)}{dr('HSC', j.b_hsc)}{dr('Pedal / Lockout', j.b_pedal)}{dr('HBO / HBC / ABO', j.b_hbo)}
              {subh('Rebound', 'var(--muted)')}
              {dr('LSR / Rebound', j.b_lsr)}{dr('HSR', j.b_hsr)}
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--green)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>After</div>
              {subh('Spring', 'var(--green)')}
              {dr('Air Pressure / Spring', j.a_air)}{dr('Sag %', j.a_sag)}{dr('Coil Preload', j.a_preload)}{dr('Tokens / Spacers', j.a_tokens)}
              {subh('Compression', 'var(--green)')}
              {dr('LSC', j.a_lsc)}{dr('HSC', j.a_hsc)}{dr('Pedal / Lockout', j.a_pedal)}{dr('HBO / HBC / ABO', j.a_hbo)}
              {subh('Rebound', 'var(--green)')}
              {dr('LSR / Rebound', j.a_lsr)}{dr('HSR', j.a_hsr)}
            </div>
          </div>
        )}

        <div className="sec">Service Log</div>
        {measurementServiceLogSections.length ? renderMeasurementRows(measurementServiceLogSections, 'No service-log measurements recorded yet.') : (
          <>
            {dr('Shim Stack Torque', j.shim_torque ? j.shim_torque + ' Nm' : null)}
            {dr('IFP Depth', j.ifp_depth ? j.ifp_depth + ' mm' : null)}
            {dr('IFP Pressure', j.ifp_psi ? j.ifp_psi + ' psi' : null)}
            {dr('Lube Oil L / R', (j.oil_l || j.oil_r) ? (j.oil_l || '?') + ' ml / ' + (j.oil_r || '?') + ' ml' : null)}
            {dr('Damper Oil Volume', j.damp_vol ? j.damp_vol + ' ml' : null)}
            {dr('Seal Head Torque', j.seal_torque ? j.seal_torque + ' Nm' : null)}
            {dr('Top Cap Torque', j.topcap_torque ? j.topcap_torque + ' Nm' : null)}
            {dr('Shaft Nut Torque', j.shaft_torque ? j.shaft_torque + ' Nm' : null)}
            {dr('Travel Config', j.travel_config)}{dr('Mount Hardware', j.mount_hw)}
            {dr('Damping Oil', j.damp_oil)}{dr('Air Can / Spring Grease', j.aircan_grease)}
            {dr('Service Kit', j.kit)}{dr('Spring Hardware', j.spring_hw)}
            {dr('Bleed Method', j.bleed)}{dr('Nitrogen / Air Charge', j.nitrogen)}
          </>
        )}

        <div className="sec">Photos, Files &amp; Service Evidence</div>

        <JobEvidence jobId={j.id} workshopId={j.workshop_id} mode="view" onLoaded={setEvidenceItems} />

        <div className="sec">Inspection &amp; Condition</div>
        <div className="grid-2">
          <div>
            {chk('Stanchion / shaft OK', j.ins_stanchion)}
            {chk('CSU / crown / steerer OK', j.ins_csu)}
            {chk('Bushing play acceptable', j.ins_bushing_play)}
            {chk('Air shaft wear acceptable', j.ins_air_shaft)}
            {chk('Damper shaft wear acceptable', j.ins_damp_shaft)}
            {chk('Eyelet / hardware wear OK', j.ins_eyelet)}
          </div>
          <div>
            {chk('Bearing mount condition OK', j.ins_bearing)}
            {chk('Wiper / foam ring OK', j.ins_wiper)}
            {chk('Oil contamination noted', j.ins_oil_contam)}
            {chk('Internal scoring / damage noted', j.ins_scoring)}
            {chk('Adjusters functioning', j.ins_adjusters)}
            {chk('Lockout / threshold functioning', j.ins_lockout)}
          </div>
        </div>
        {j.condition_notes && <div className="detail-val" style={{ marginTop: 8, lineHeight: 1.6 }}>{j.condition_notes}</div>}

        <div className="sec">Final QC</div>
        {j.qc_status && (
          <StatusPill as="div" tone={j.qc_status === 'passed' ? 'green' : 'orange'} style={{ marginBottom: 10 }}>
            QC {j.qc_status === 'passed' ? 'Passed' : 'Failed'}
          </StatusPill>
        )}
        {j.qc_signed_off_at && dr('QC Signed Off', `${j.qc_signed_off_by_name || 'Workshop'} - ${fmtDateTime(j.qc_signed_off_at)}`)}
        {j.qc_notes && dr('QC Notes', j.qc_notes)}
        <div className="grid-2">
          <div>
            {chk('Correct torque settings confirmed', j.qc_torque)}
            {chk('Correct pressures / spring installed', j.qc_pressure)}
            {chk('Correct clickers reset', j.qc_clickers)}
            {chk('Full travel smooth', j.qc_travel)}
            {chk('No topout / knock', j.qc_topout)}
          </div>
          <div>
            {chk('No oil leaks', j.qc_oil_leak)}
            {chk('No air loss detected', j.qc_air_loss)}
            {chk('All adjusters functional', j.qc_adjusters)}
            {chk('Returned to customer baseline', j.qc_baseline)}
            {chk('FLI sticker applied', j.qc_sticker)}
          </div>
        </div>

        <div className="sec">Recommendation &amp; Handover</div>
        {dr('Service Interval', j.interval)}
        {dr('Next Service Due', j.next_service)}
        {dr('Next Service Date', j.next_service_date ? fmtDate(j.next_service_date) : '')}
        {dr('Service Reminder', j.reminder_sent_at ? `Sent ${fmtDateTime(j.reminder_sent_at)}` : '')}
        {j.reminder_last_error && dr('Reminder Error', j.reminder_last_error)}
        {j.further_work && <div className="detail-row"><span className="detail-label">Further Work Required</span><span className="detail-val" style={{ lineHeight: 1.6 }}>{j.further_work}</span></div>}
        {j.worn_parts && <div className="detail-row"><span className="detail-label">Worn Parts Noted</span><span className="detail-val" style={{ lineHeight: 1.6 }}>{j.worn_parts}</span></div>}
        {j.setup_rec && <div className="detail-row"><span className="detail-label">Setup Recommendation</span><span className="detail-val" style={{ lineHeight: 1.6 }}>{j.setup_rec}</span></div>}

        {partsUsed.length > 0 && (
          <>
            <div className="sec">Parts Used</div>
            {partsUsed.map((part, index) => {
              const lineCost = part.quantity * part.unit_cost
              const lineSell = part.gross_amount ?? part.quantity * part.unit_price
              return (
                <div key={part.id || index} className="detail-row">
                  <span className="detail-label">{part.quantity}x {part.part_number || 'Part'}</span>
                  <span className="detail-val">
                    <span className="text-white fw7">{part.name || 'Workshop part'}</span>
                    {part.supplier ? <span className="text-muted"> · {part.supplier}</span> : null}
                    <br />
                    <span className="text-muted">Cost £{lineCost.toFixed(2)} · Sell £{lineSell.toFixed(2)}</span>
                  </span>
                </div>
              )
            })}
          </>
        )}

        <div className="sec">Financials</div>
        {financials.partsSellTotal > 0 && dr('Parts Sold', '£' + financials.partsSellTotal.toFixed(2))}
        {dr('Parts Cost', '£' + financials.basePartsCost.toFixed(2))}
        {dr('Service Cost', '£' + financials.serviceCost.toFixed(2))}
        {financials.quoteAmount > 0 && dr('Additional Quote', '£' + financials.quoteAmount.toFixed(2))}
        {financials.quotePartsCost > 0 && dr('Additional Parts Cost', '£' + financials.quotePartsCost.toFixed(2))}
        {financials.quotePartsCost > 0 && dr('Total Parts Cost', '£' + financials.partsCost.toFixed(2))}
        {financials.subtotal > 0 && (financials.approvedDiscountTotal > 0 || financials.approvedSurchargeTotal > 0) && dr('Subtotal', '£' + financials.subtotal.toFixed(2))}
        {financials.approvedAdjustments.map(row => (
          <React.Fragment key={row.id || `${row.kind}-${row.value}-${row.calculatedAmount}`}>
            {dr(row.label || (row.kind === 'discount' ? 'Discount' : 'Adjustment'), `${row.kind === 'discount' ? '-' : '+'}£${row.calculatedAmount.toFixed(2)}`)}
          </React.Fragment>
        ))}
        <div className="detail-row">
          <span className="detail-label">Total Cost</span>
          <span className="detail-val text-white fw7">£{financials.totalCost.toFixed(2)}</span>
        </div>
        <div className="detail-row">
          <span className="detail-label">Verified margin</span>
          <span className="detail-val"><a href="/reporting/profitability">View net sales and recorded costs in Job Profitability</a></span>
        </div>
        {loyaltyProgress.enabled && (
          <div className="alert alert-info" style={{ marginTop: 12 }}>
            <div className="fw7 text-white">Loyalty Rewards</div>
            <div className="fs12" style={{ marginTop: 4 }}>
              {loyaltyProgress.availableCount > 0
                ? `${loyaltyProgress.availableCount} reward${loyaltyProgress.availableCount === 1 ? '' : 's'} available: ${loyaltyProgress.rewardLabel}.`
                : `${loyaltyProgress.qualifiedCount}/${loyaltyProgress.required} completed paid eligible services. ${loyaltyRuleSummary(effectiveLoyaltyWorkshop)}.`}
            </div>
            {loyaltyProgress.availableCount > 0 && (
              <div className="action-row" style={{ marginTop: 10 }}>
                <button
                  type="button"
                  className="btn btn-green btn-sm"
                  onClick={applyLoyaltyReward}
                  disabled={loyaltyBusy || loyaltyAlreadyAppliedToJob || !loyaltyEligibleJob || !canApplyLoyaltyByRole || adjustmentLocked}
                >
                  {loyaltyBusy ? 'Applying...' : 'Apply Loyalty Reward'}
                </button>
                {loyaltyAlreadyAppliedToJob && <span className="fs11 text-muted">Already applied to this job.</span>}
                {!loyaltyEligibleJob && <span className="fs11 text-muted">This job is not in an eligible loyalty category.</span>}
                {!canApplyLoyaltyByRole && <span className="fs11 text-muted">Owner required for this workshop.</span>}
              </div>
            )}
          </div>
        )}
        {(adjustmentRows.length > 0 || adjustmentNotice || adjustmentError || j.payment_refresh_required) && (
          <div style={{ marginTop: 12 }}>
            {j.payment_refresh_required && (
              <div className="alert alert-info" style={{ marginBottom: 10 }}>
                {j.payment_refresh_reason || 'Pricing changed. Generate a new payment link before requesting payment.'}
              </div>
            )}
            {adjustmentNotice && <div className="alert alert-success" style={{ marginBottom: 10 }}>{adjustmentNotice}</div>}
            {adjustmentError && <div className="alert alert-error" style={{ marginBottom: 10 }}>{adjustmentError}</div>}
            <div style={{ display: 'grid', gap: 8 }}>
              {adjustmentRows.map(row => {
                const amount = priceAdjustmentAmount(row, financials.subtotal)
                const target = row.target_scope === 'job'
                  ? 'Job subtotal'
                  : (row.target_lines || []).map(line => line.name).filter(Boolean).join(', ') || 'Selected lines'
                return (
                  <div key={row.id} style={{ border: '1px solid var(--border)', borderRadius: 6, padding: 10, background: 'var(--black)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                      <div>
                        <div className="text-white fw7">{row.label || (row.kind === 'discount' ? 'Discount' : 'Adjustment')}</div>
                        <div className="fs11 text-muted">{target}</div>
                        {row.reason && <div className="fs11 text-muted" style={{ marginTop: 3 }}>Internal note: {row.reason}</div>}
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div className={row.kind === 'discount' ? 'text-white fw7' : 'text-green fw7'}>{row.kind === 'discount' ? '-' : '+'}£{amount.toFixed(2)}</div>
                        <StatusPill tone={row.status === 'approved' ? 'green' : row.status === 'pending' ? 'orange' : 'muted'}>{row.status}</StatusPill>
                      </div>
                    </div>
                    {isOwner && row.status === 'pending' && !adjustmentLocked && (
                      <div className="action-row" style={{ marginTop: 10 }}>
                        <button className="btn btn-green btn-sm" onClick={() => decideAdjustment(row, 'approved')} disabled={adjustmentBusy === `approved-${row.id}`}>
                          {adjustmentBusy === `approved-${row.id}` ? 'Approving...' : 'Approve'}
                        </button>
                        <button className="btn btn-ghost btn-sm" onClick={() => decideAdjustment(row, 'rejected')} disabled={adjustmentBusy === `rejected-${row.id}`}>
                          {adjustmentBusy === `rejected-${row.id}` ? 'Rejecting...' : 'Reject'}
                        </button>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Payment link */}
        <div style={{ marginTop: 14 }}>
          {paymentNotice && <div className="alert alert-success" style={{ marginBottom: 10 }}>{paymentNotice}</div>}
          {paymentError && <div className="alert alert-error" style={{ marginBottom: 10 }}>{paymentError}</div>}
          <div className="alert alert-info" style={{ marginBottom: 10 }}>
            {STANDARD_CONNECT_MESSAGE}
          </div>
          {outboundTestMode && (
            <div className="alert alert-info" style={{ marginBottom: 10 }}>
              Test mode: payment creation and outbound payment messages are disabled.
            </div>
          )}
          <PaymentRequestEmail job={j} customer={customer} canCreate={paymentEmailState.canCreateLink && !isCompleted}
            canSend={paymentEmailState.canSendReminder && !isCompleted} disabled={outboundTestMode || generatingLink || Boolean(paymentBusy)}
            onBusy={value => { setPaymentBusy(value ? 'payment-only' : ''); if (value) { setPaymentNotice(''); setPaymentError('') } }}
            onResult={({ generated, sent }) => {
              if (generated) setPaymentLink(generated.url)
              setJob(current => ({ ...current,
                ...(generated ? { payment_link: generated.url, payment_status: generated.payment_status || 'requested', stripe_checkout_session_id: generated.checkout_session_id, payment_refresh_required: false, payment_refresh_reason: '' } : {}),
                ...(sent ? { payment_reminder_sent_at: sent.payment_reminder_sent_at } : {}),
              }))
              if (sent) setPaymentNotice('Payment request emailed. No report or photos attached.')
              if (onChanged) onChanged()
            }} />
          {!activePaymentLink ? (
            <>
              <button
                className="btn btn-primary btn-sm"
                onClick={generatePaymentLink}
                disabled={outboundTestMode || generatingLink || Boolean(paymentBusy) || isCancelled || financials.totalCost < 0.5}
                title={outboundTestMode ? 'Disabled in test mode.' : financials.totalCost < 0.5 ? 'Stripe requires at least GBP 0.50.' : ''}
              >
                {generatingLink ? 'Generating...' : 'Generate Link Only'}
              </button>
              {linkError && <div style={{ marginTop: 6, fontSize: 12, color: 'var(--red)' }}>{linkError}</div>}
              {isCancelled && <div className="fs12 text-muted" style={{ marginTop: 6 }}>Cancelled jobs cannot receive payment links.</div>}
              {!isCancelled && financials.totalCost < 0.5 && <div className="fs12 text-muted" style={{ marginTop: 6 }}>Stripe requires at least GBP 0.50.</div>}
            </>
          ) : (
            <div style={{ marginTop: 4 }}>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 6 }}>
                Stripe Standard payment link <StatusPill tone={payment.tone} style={{ marginLeft: 6 }}>{payment.label}</StatusPill>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <span className="fs12 text-muted">
                  {j.payment_reminder_sent_at ? 'Checkout link is ready for this job.' : 'Payment link ready — customer not contacted.'}
                </span>
                {payment.key !== 'paid' && !isCancelled && (
                  <>
                    <button className="btn btn-ghost btn-sm" onClick={sendPaymentReminder} disabled={outboundTestMode || Boolean(paymentBusy)}>
                      {paymentBusy === 'reminder' ? 'Sending...' : sendPaymentLabel}
                    </button>
                  </>
                )}
                <button className="btn btn-ghost btn-sm" onClick={copyLink}>
                  {linkCopied ? 'Copied' : 'Copy Link'}
                </button>
              </div>
              {j.payment_reminder_sent_at && <div className="fs11 text-muted" style={{ marginTop: 5 }}>Last sent: {fmtDateTime(j.payment_reminder_sent_at)}</div>}
            </div>
          )}
        </div>

        <div className="sec">Payment &amp; Job Closure</div>
        <div style={{ background: 'var(--black)', border: '1px solid var(--border)', borderRadius: 6, padding: 12 }}>
          <div className="action-row" style={{ marginBottom: 10 }}>
            <StatusPill tone={status.tone}>{status.label}</StatusPill>
            <StatusPill tone={payment.tone}>Payment: {payment.label}</StatusPill>
            {j.report_sent_at && <StatusPill tone="green">Report sent</StatusPill>}
            {isCompleted && <StatusPill tone="green">Closed</StatusPill>}
            {j.administrative_closed_at && <StatusPill tone="orange">Administrative close</StatusPill>}
          </div>
          {dr('Ready For Collection', fmtDateTime(j.ready_for_collection_at))}
          {j.payment_paid_at && dr('Payment Paid', fmtDateTime(j.payment_paid_at))}
          {j.payment_method && dr('Payment Method', paymentMethodLabel(j.payment_method))}
          {j.payment_reference && dr('Payment Reference', j.payment_reference)}
          {j.collected_at && dr('Collected', fmtDateTime(j.collected_at))}
          {j.closed_at && dr('Closed', fmtDateTime(j.closed_at))}
          {j.closed_by_name && dr('Closed By', j.closed_by_name)}
          <div className="action-row" style={{ marginTop: 12 }}>
            {canMarkReady && !isCompleted && !isCancelled && statusKey !== 'ready_for_collection' && (
              <button className="btn btn-ghost btn-sm" onClick={markReadyForCollection} disabled={paymentBusy === 'ready'}>
                {paymentBusy === 'ready' ? 'Saving...' : 'Mark Ready for Collection'}
              </button>
            )}
            {canMarkPaidManually && payment.key !== 'paid' && payment.key !== 'requires_review' && payableAmountPence > 0 && !isCancelled && (
              <button className="btn btn-green btn-sm" onClick={markPaid} disabled={paymentBusy === 'paid'}>
                {paymentBusy === 'paid' ? 'Saving...' : 'Mark Paid Manually'}
              </button>
            )}
            {canCloseJob && !isCompleted && !isCancelled && (
              <button className="btn btn-primary btn-sm" onClick={completeJob} disabled={paymentBusy === 'complete' || (payableAmountPence > 0 && payment.key !== 'paid')}>
                {paymentBusy === 'complete' ? 'Closing...' : 'Complete / Close Job'}
              </button>
            )}
            {canReopenJob && isCompleted && (
              <button className="btn btn-ghost btn-sm" onClick={reopenJob} disabled={paymentBusy === 'reopen'}>
                {paymentBusy === 'reopen' ? 'Reopening...' : 'Reopen Job'}
              </button>
            )}
          </div>
          {!isCompleted && !isCancelled && payment.key !== 'paid' && payableAmountPence > 0 && (
            <div className="fs11 text-muted" style={{ marginTop: 8 }}>
              Payment must be marked paid before the job can be closed.
            </div>
          )}
          {!isCompleted && !isCancelled && payment.key === 'requires_review' && (
            <div className="fs11 text-muted" style={{ marginTop: 8 }}>
              Resolve the payment review before changing payment or closing this job.
            </div>
          )}
          <AdministrativeClosePanel
            profile={profile}
            job={j}
            missingStages={administrativeCloseMissing}
            onClosed={nextJob => {
              setJob(nextJob)
              setPaymentNotice('Job administratively closed. Incomplete stages remain recorded.')
              if (onChanged) onChanged()
            }}
          />
        </div>

        {j.notes && <><div className="sec">General Notes</div><div className="detail-val" style={{ lineHeight: 1.7 }}>{j.notes}</div></>}

        <div className="sec">Report Versions</div>
        {reportNotice && <div className="alert alert-success" style={{ marginBottom: 10 }}>{reportNotice}</div>}
        {reportHistoryError && <div className="alert alert-error" style={{ marginBottom: 10 }}>{reportHistoryError}</div>}
        {reportVersions.length ? (
          <div style={{ display: 'grid', gap: 8 }}>
            {reportVersions.map(version => (
              <div
                key={version.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr auto',
                  gap: 12,
                  alignItems: 'center',
                  padding: '10px 12px',
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  background: 'var(--page)',
                }}
              >
                <span className="job-status-chip job-status-green">V{version.version_number}</span>
                <div style={{ minWidth: 0 }}>
                  <div className="text-white fw7">
                    {reportDeliveryLabel(version.delivery_status)}
                    {version.recipient_email ? <span className="text-muted"> to {version.recipient_email}</span> : null}
                  </div>
                  <div className="fs11 text-muted" style={{ marginTop: 3 }}>
                    {fmtDateTime(reportDisplayTime(version))}
                    {version.total_charged ? ` · ${fmtMoney(version.total_charged)}` : ''}
                    {version.file_name ? ` · ${version.file_name}` : ''}
                    <ReportDeliveryHistory version={version} formatDate={fmtDateTime} />
                  </div>
                </div>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => openReportVersion(version)}
                  disabled={reportBusy === `open-${version.id}`}
                >
                  {reportBusy === `open-${version.id}` ? 'Opening...' : 'Open PDF'}
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="fs12 text-muted">No saved report versions yet.</div>
        )}

        <hr />

        {/* Email send bar */}
        {emailMode && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
            <input
              type="email"
              value={emailTo}
              onChange={e => setEmailTo(e.target.value)}
              placeholder="customer@email.com"
              style={{ flex: 1, minWidth: 200 }}
              autoFocus
            />
            <button className="btn btn-primary" onClick={sendReport} disabled={sending || !emailTo}>
              {sending ? 'Sending…' : 'Send'}
            </button>
            <button className="btn btn-ghost" onClick={() => { setEmailMode(false); setEmailStatus(null) }}>Cancel</button>
          </div>
        )}

        {emailStatus === 'sent' && (
          <div style={{ marginBottom: 12, fontSize: 13, color: 'var(--green)', fontWeight: 600 }}>
            ✓ Report sent to {emailTo}
          </div>
        )}
        {emailStatus === 'error' && (
          <div style={{ marginBottom: 12, fontSize: 13, color: 'var(--red)' }}>
            ✗ {emailError}
          </div>
        )}
        {tagError && <div className="alert alert-error" style={{ marginBottom: 12 }}>{tagError}</div>}
        {unresolvedChargeableStockCount > 0 && (
          <div className="alert alert-error" style={{ marginBottom: 12 }}>
            Resolve {unresolvedChargeableStockCount} chargeable draft stock item{unresolvedChargeableStockCount === 1 ? '' : 's'} before previewing, saving or emailing an itemised report.
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button className="btn btn-primary" onClick={handlePrintTag} disabled={tagBusy}>
            {tagBusy ? 'Preparing Tag...' : 'Print Tag'}
          </button>
          {onEdit && (
            <button className="btn btn-ghost" onClick={onEdit}>Edit Job</button>
          )}
          {!comp && onCreateComponent && (
            <button className="btn btn-green" onClick={onCreateComponent}>Create Component</button>
          )}
          {comp && (
            <>
              <button className="btn btn-green" onClick={() => generateCustomerReport({ ...j, evidence: evidenceItems }, customer, comp, workshop)} disabled={unresolvedChargeableStockCount > 0}>Preview Report</button>
              <button className="btn btn-ghost" onClick={createReportVersion} disabled={reportBusy === 'create' || unresolvedChargeableStockCount > 0}>
                {reportBusy === 'create' ? 'Saving...' : 'Save PDF Version'}
              </button>
              <button className="btn btn-primary" onClick={() => { setEmailMode(true); setEmailStatus(null) }} disabled={outboundTestMode || unresolvedChargeableStockCount > 0} title={outboundTestMode ? 'Outbound email is disabled in test mode.' : ''}>Email Report</button>
            </>
          )}
          {comp?.brand === 'fox' && comp?.serial && (
            <a className="btn btn-ghost" href={`https://tech.ridefox.com/bike/search/serial/${encodeURIComponent(comp.serial)}`} target="_blank" rel="noopener noreferrer">Fox Docs ↗</a>
          )}
          {comp?.brand === 'rockshox' && (
            <a className="btn btn-ghost" href={rockShoxTrailheadUrl(comp.serial)} target="_blank" rel="noopener noreferrer">RockShox Trailhead</a>
          )}
          <button className="btn btn-ghost" onClick={onClose}>{returnLabel}</button>
        </div>
      </div>
    </div>
  )
}

export default function ViewJobModal(props) {
  if (isGeneralJob(props.job)) return <GeneralJobModal {...props} initialData={props.job} readOnly />
  return <SuspensionViewJobModal {...props} />
}
